/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import {useEffect, useState} from 'react';

/**
 * @hook useKeyPress
 *
 * Custom hook that sets key listeners to be able to react
 * to presses on keys that are passed to the hook. Returns
 * a boolean value based on the key that is passed to the hook
 *
 * ```typescript
 *
 *   import {useKeyPress} from '@ion/dls/src/hooks/';
 *
 *   const SomeComponent = () => {
 *     const hKey = useKeyPress('h');
 *   }
 *
 * ```
 */
export function useKeyPress(targetKey) {
  const [keyPressed, setKeyPressed] = useState(false);

  // If pressed key is our target key then set to true
  function downHandler({key}) {
    if (key === targetKey) {
      setKeyPressed(true);
    }
  }

  // If released key is our target key then set to false
  const upHandler = ({key}) => {
    if (key === targetKey) {
      setKeyPressed(false);
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', downHandler);
    window.addEventListener('keyup', upHandler);
    // Remove event listeners on cleanup
    return () => {
      window.removeEventListener('keydown', downHandler);
      window.removeEventListener('keyup', upHandler);
    };
  }, []);

  return keyPressed;
};
